import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import {
  GripVerticalIcon,
  PlusIcon,
  Trash2Icon,
  Settings2Icon,
  LinkIcon,
  AlertTriangleIcon,
} from 'lucide-react'
import {
  ContractVisitGroup,
  ContractServiceLite,
  VisitGroupTemplateOption,
} from './VisitGroupEditor'
import {
  createGroupWithService,
  formatPiggybackLabel,
  getGroupAssignments,
  getUnassignedServiceIds,
  groupRecurrenceMatchesDate,
  mergeGroups,
  moveServiceToGroup,
  piggybackNearestMatchesDate,
  piggybackMatchesDate,
  removeServiceFromGroups,
  resolveVisitGroupDisplayDays,
  syncGroupServiceIds,
  VisitGroupServiceAssignment,
} from '../lib/visitGroupBoardOps'
import { servicesHaveIncompatibleSchedules } from '../lib/visitGroupSuggestion'
import { lookupVisitBoardService } from '../lib/dsnyContractPersistence'
import { fetchCrewsAndEmployees } from '../api/jobWorkflow'
import type { EmployeeOption } from '../api/jobWorkflow'
import { formatEmployeeLabel } from '../lib/formatEmployeeLabel'

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/**
 * Build a piggyback assignment that preserves the service's own schedule when it's
 * dropped onto a group with a different cadence. Falls back to a sensible monthly
 * default (1st occurrence on the group's first visit day) when the service is marked
 * monthly but hasn't specified the detail yet.
 */
function piggybackAssignmentFromService(
  svc: any,
  targetGroup: ContractVisitGroup
): Partial<VisitGroupServiceAssignment> {
  const svcType: string = svc?.recurrence_type || 'weekly'

  if (svc?.recurrence_day_of_month != null) {
    return {
      schedule_mode: 'piggyback',
      recurrence_type: 'monthly',
      recurrence_day_of_month: svc.recurrence_day_of_month,
      // Dropping onto a group means "ride this group's visits", so snap to the
      // nearest visit day by default — uncheck it in the group settings to run
      // on the exact date instead.
      recurrence_nearest_visit: true,
    }
  }

  if (svc?.recurrence_weekday != null && svc?.recurrence_occurrence != null) {
    return {
      schedule_mode: 'piggyback',
      recurrence_type: 'monthly',
      recurrence_weekday: svc.recurrence_weekday,
      recurrence_occurrence: svc.recurrence_occurrence,
      recurrence_nearest_visit: true,
    }
  }

  if (svcType === 'monthly') {
    return {
      schedule_mode: 'piggyback',
      recurrence_type: 'monthly',
      recurrence_weekday: targetGroup.recurrence_days?.[0] ?? 1,
      recurrence_occurrence: 1,
      recurrence_nearest_visit: true,
    }
  }

  // Other non-weekly cadences (bi-weekly, daily, custom): carry the type/days across.
  return {
    schedule_mode: 'piggyback',
    recurrence_type: svcType,
    recurrence_days:
      svc?.recurrence_days && svc.recurrence_days.length ? svc.recurrence_days : undefined,
    recurrence_nearest_visit: true,
  }
}

/** Drop visit groups that no longer have any services assigned (e.g. after moving the
 * last service out). On the board, empty groups are just leftover drop targets. */
export function pruneEmptyVisitGroups(groups: ContractVisitGroup[]): ContractVisitGroup[] {
  return groups.filter((g) => {
    if (getGroupAssignments(g).length > 0) return true
    // Keep DSNY day slots and other scheduled weekly shells as drop targets.
    if (g.dsny_integration) return true
    if (g.recurrence_type === 'weekly' && (g.recurrence_days?.length ?? 0) > 0) return true
    return false
  })
}

interface VisitScheduleBoardProps {
  visitGroups: ContractVisitGroup[]
  onChange: (groups: ContractVisitGroup[]) => void
  contractServices: ContractServiceLite[]
  contractStartDate?: string | null
  contractEndDate?: string | null
  contractId?: string
  onReDerive?: () => void
  onAdvancedSettings?: (groupId: string) => void
  templates?: VisitGroupTemplateOption[]
  showSaveButton?: boolean
  onSave?: () => void
  saving?: boolean
}

interface PiggybackDialogState {
  serviceId: string
  targetGroupId: string
  serviceName: string
}

function DraggableServiceChip({
  serviceId,
  serviceName,
  assignment,
  sourceGroupId,
}: {
  serviceId: string
  serviceName: string
  assignment?: VisitGroupServiceAssignment
  sourceGroupId?: string
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `service:${serviceId}:${sourceGroupId || 'pool'}`,
    data: { serviceId, sourceGroupId },
  })

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1,
  }

  const isPiggyback = assignment?.schedule_mode === 'piggyback'

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${
        isPiggyback
          ? 'bg-amber-50 text-amber-800 border-amber-200'
          : 'bg-white text-gray-700 border-gray-300'
      }`}
    >
      <button type="button" className="cursor-grab touch-none" {...listeners} {...attributes}>
        <GripVerticalIcon className="w-3 h-3 text-gray-400" />
      </button>
      <span className="max-w-[140px] truncate">{serviceName}</span>
      {isPiggyback && (
        <span className="text-[10px] text-amber-600" title="Piggyback schedule">
          ({formatPiggybackLabel(assignment!)})
        </span>
      )}
    </div>
  )
}

function DroppableZone({
  id,
  children,
  className,
  label,
}: {
  id: string
  children: React.ReactNode
  className?: string
  label?: string
}) {
  const { setNodeRef, isOver } = useDroppable({ id })
  return (
    <div
      ref={setNodeRef}
      className={`${className || ''} ${isOver ? 'ring-2 ring-blue-400 ring-offset-1' : ''}`}
    >
      {label && <p className="text-xs font-medium text-gray-500 mb-2">{label}</p>}
      {children}
    </div>
  )
}

export function VisitScheduleBoard({
  visitGroups,
  onChange,
  contractServices,
  contractStartDate,
  contractEndDate,
  contractId = '',
  onReDerive,
  onAdvancedSettings,
  templates,
  showSaveButton,
  onSave,
  saving,
}: VisitScheduleBoardProps) {
  const [activeDrag, setActiveDrag] = useState<{ serviceId: string; serviceName: string } | null>(
    null
  )
  const [piggybackDialog, setPiggybackDialog] = useState<PiggybackDialogState | null>(null)
  const [piggybackMode, setPiggybackMode] = useState<'nth_weekday' | 'day_of_month'>('nth_weekday')
  const [piggybackOccurrence, setPiggybackOccurrence] = useState(1)
  const [piggybackWeekday, setPiggybackWeekday] = useState(1)
  const [piggybackDayOfMonth, setPiggybackDayOfMonth] = useState(15)
  const [piggybackNearest, setPiggybackNearest] = useState(true)
  const [crews, setCrews] = useState<{ id: string; name: string }[]>([])
  const [employees, setEmployees] = useState<EmployeeOption[]>(
    []
  )
  const [assignmentLoadError, setAssignmentLoadError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchCrewsAndEmployees().then(({ crews: c, employees: e, loadError }) => {
      if (cancelled) return
      setCrews(c)
      setEmployees(e)
      if (loadError) setAssignmentLoadError(loadError)
      else if (c.length === 0 && e.length === 0) setAssignmentLoadError('empty')
      else setAssignmentLoadError(null)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  const updateGroupAssignment = (
    groupId: string,
    patch: { assigned_crew_id?: string | null; assigned_employee_id?: string | null }
  ) => {
    onChange(
      visitGroups.map((g) => (g.id === groupId ? { ...g, ...patch } : g))
    )
  }

  const serviceMap = useMemo(() => {
    const m = new Map<string, ContractServiceLite>()
    contractServices.forEach((s) => m.set(s.id, s))
    return m
  }, [contractServices])

  const allServiceIds = contractServices.map((s) => s.id)
  const unassignedIds = getUnassignedServiceIds(visitGroups, allServiceIds)

  const getServiceName = (id: string) => {
    const svc = lookupVisitBoardService(id, contractServices as any)
    return svc?.service_name || svc?.name || 'Service'
  }

  const handleDragStart = (event: DragStartEvent) => {
    const serviceId = event.active.data.current?.serviceId as string
    if (serviceId) {
      setActiveDrag({ serviceId, serviceName: getServiceName(serviceId) })
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDrag(null)
    const { active, over } = event
    if (!over) return

    const serviceId = active.data.current?.serviceId as string
    const sourceGroupId = active.data.current?.sourceGroupId as string | undefined
    if (!serviceId) return

    const overId = String(over.id)

    if (overId === 'unassigned-pool') {
      onChange(pruneEmptyVisitGroups(removeServiceFromGroups(visitGroups, serviceId)))
      return
    }

    if (overId.startsWith('group:')) {
      const targetGroupId = overId.replace('group:', '')
      if (sourceGroupId === targetGroupId) return

      const service = contractServices.find((s) => s.id === serviceId)
      const targetGroup = visitGroups.find((g) => g.id === targetGroupId)
      if (!service || !targetGroup) return

      // A piggyback configured in the source group (its own monthly cadence) travels
      // with the service instead of being silently reset to a weekly primary.
      const sourceGroup = sourceGroupId
        ? visitGroups.find((g) => g.id === sourceGroupId)
        : undefined
      const sourceAssignment = sourceGroup
        ? getGroupAssignments(sourceGroup).find((a) => a.service_id === serviceId)
        : undefined
      if (sourceAssignment && sourceAssignment.schedule_mode === 'piggyback') {
        onChange(
          pruneEmptyVisitGroups(
            moveServiceToGroup(visitGroups, serviceId, targetGroupId, {
              ...sourceAssignment,
              // Riding a new group's visits: snap to the nearest visit day unless
              // the user already unchecked it for this service.
              recurrence_nearest_visit: sourceAssignment.recurrence_nearest_visit ?? true,
            })
          )
        )
        return
      }

      const primaryInTarget = getGroupAssignments(targetGroup).filter(
        (a) => a.schedule_mode !== 'piggyback'
      )
      const svc = service as any
      const svcType: string = svc?.recurrence_type || 'weekly'
      const groupType: string = targetGroup.recurrence_type || 'weekly'
      // The service already carries its own non-weekly schedule (monthly, snap-to-visit,
      // etc.). It should never be silently downgraded to a weekly primary — carry that
      // schedule over as a piggyback automatically, no prompt.
      const hasOwnSchedule =
        svc?.snap_to_closest === true || (svcType !== 'weekly' && svcType !== groupType)

      if (hasOwnSchedule) {
        onChange(
          pruneEmptyVisitGroups(
            moveServiceToGroup(
              visitGroups,
              serviceId,
              targetGroupId,
              piggybackAssignmentFromService(svc, targetGroup)
            )
          )
        )
        return
      }

      // Weekly service dropped onto a group with non-overlapping days: here the service
      // has no explicit cadence of its own, so let the user choose piggyback vs. merge.
      const incompatible =
        primaryInTarget.length > 0 &&
        primaryInTarget.some((a) => {
          const other = contractServices.find((s) => s.id === a.service_id)
          return other && servicesHaveIncompatibleSchedules(service, other)
        })

      if (incompatible) {
        setPiggybackMode('nth_weekday')
        setPiggybackDialog({
          serviceId,
          targetGroupId,
          serviceName: getServiceName(serviceId),
        })
        return
      }

      onChange(pruneEmptyVisitGroups(moveServiceToGroup(visitGroups, serviceId, targetGroupId)))
      return
    }

    if (overId === 'new-group-zone') {
      const service = contractServices.find((s) => s.id === serviceId) as any
      const serviceDays: number[] =
        service?.recurrence_days || service?.schedule?.recurrence_days || []
      onChange(
        createGroupWithService(
          pruneEmptyVisitGroups(removeServiceFromGroups(visitGroups, serviceId)),
          serviceId,
          contractId,
          contractStartDate,
          contractEndDate,
          getServiceName(serviceId),
          {
            recurrence_type: service?.recurrence_type || service?.schedule?.recurrence_type || 'weekly',
            recurrence_days: serviceDays,
            recurrence_weekday: service?.recurrence_weekday ?? service?.schedule?.recurrence_weekday ?? null,
            recurrence_occurrence:
              service?.recurrence_occurrence ?? service?.schedule?.recurrence_occurrence ?? null,
            recurrence_day_of_month:
              service?.recurrence_day_of_month ?? service?.schedule?.recurrence_day_of_month ?? null,
          }
        )
      )
    }
  }

  const confirmPiggyback = () => {
    if (!piggybackDialog) return
    const targetGroup = visitGroups.find((g) => g.id === piggybackDialog.targetGroupId)
    const groupDays = targetGroup?.recurrence_days || []

    const assignment =
      piggybackMode === 'day_of_month'
        ? {
            schedule_mode: 'piggyback' as const,
            recurrence_type: 'monthly',
            recurrence_day_of_month: piggybackDayOfMonth,
            recurrence_nearest_visit: piggybackNearest,
            recurrence_days: groupDays,
          }
        : {
            schedule_mode: 'piggyback' as const,
            recurrence_type: 'monthly',
            recurrence_weekday: piggybackWeekday,
            recurrence_occurrence: piggybackOccurrence,
            recurrence_nearest_visit: piggybackNearest,
            recurrence_days: groupDays,
          }

    onChange(
        pruneEmptyVisitGroups(
        moveServiceToGroup(
          visitGroups,
          piggybackDialog.serviceId,
          piggybackDialog.targetGroupId,
          assignment
        )
      )
    )
    setPiggybackDialog(null)
  }

  const projectedPreview = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const horizon = new Date(today)
    horizon.setDate(today.getDate() + 28)
    const anchor = contractStartDate
      ? new Date(contractStartDate + 'T00:00:00')
      : today

    const entries: Array<{ date: string; groupName: string; services: string[] }> = []

    for (const g of visitGroups) {
      if (g.is_active === false) continue
      const groupAnchor = g.start_date ? new Date(g.start_date + 'T00:00:00') : anchor
      const monthly = {
        weekday: g.recurrence_weekday,
        occurrence: g.recurrence_occurrence,
        dayOfMonth: g.recurrence_day_of_month,
      }
      const cursor = new Date(today)
      while (cursor <= horizon) {
        if (
          groupRecurrenceMatchesDate(
            g.recurrence_type || 'weekly',
            g.recurrence_days || [],
            cursor,
            groupAnchor,
            monthly
          )
        ) {
          const dateStr = cursor.toISOString().split('T')[0]
          const firing = getGroupAssignments(g)
            .filter((a) => {
              if (a.schedule_mode !== 'piggyback') return true
              if (a.recurrence_nearest_visit) {
                return piggybackNearestMatchesDate(
                  a,
                  cursor,
                  g.recurrence_type || 'weekly',
                  g.recurrence_days || [],
                  groupAnchor,
                  monthly
                )
              }
              return piggybackMatchesDate(a, cursor, groupAnchor)
            })
            .map((a) => getServiceName(a.service_id))
          if (firing.length > 0) {
            entries.push({ date: dateStr, groupName: g.name, services: firing })
          }
        }
        cursor.setDate(cursor.getDate() + 1)
      }
    }

    return entries.sort((a, b) => a.date.localeCompare(b.date)).slice(0, 20)
  }, [visitGroups, contractStartDate, contractServices])

  const removeGroup = (groupId: string) => {
    if (!window.confirm('Remove this visit group? Services will move to unassigned.')) return
    onChange(visitGroups.filter((g) => g.id !== groupId))
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Visit Schedule</h2>
          <p className="text-sm text-gray-600 mt-0.5">
            Grouped by shared visit day. Drag services between visit groups to combine or split.
            Set crew/employee per group below (Inherit uses contract/property default).
          </p>
          {assignmentLoadError && (
            <p className="text-xs text-amber-700 mt-1">
              {assignmentLoadError === 'empty' ? (
                <>
                  No crews or employees found.{' '}
                  <Link to="/settings/team" className="underline font-medium">
                    Add them in Team
                  </Link>
                  .
                </>
              ) : (
                assignmentLoadError
              )}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {onReDerive && (
            <button
              type="button"
              onClick={onReDerive}
              className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
            >
              Re-derive
            </button>
          )}
          {showSaveButton && onSave && (
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Visit Groups'}
            </button>
          )}
        </div>
      </div>

      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
          {visitGroups.map((group) => {
            const assignments = getGroupAssignments(group)
            const dayLabel = resolveVisitGroupDisplayDays(group, contractServices)
              .map((d) => DAY_LABELS[d])
              .join(', ')

            return (
              <DroppableZone
                key={group.id}
                id={`group:${group.id}`}
                className="border border-gray-200 rounded-lg p-3 bg-gray-50 min-h-[120px]"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{group.name}</p>
                    <p className="text-xs text-gray-500">
                      {dayLabel || 'No days set'}
                      {group.dsny_integration && (
                        <span className="ml-1 inline-flex px-1.5 py-0.5 text-[10px] rounded bg-indigo-50 text-indigo-600">
                          DSNY
                        </span>
                      )}
                      {(group.assigned_crew_id || group.assigned_employee_id) && (
                        <span className="ml-1 inline-flex px-1.5 py-0.5 text-[10px] rounded bg-emerald-50 text-emerald-700">
                          {group.assigned_crew_id
                            ? crews.find((c) => c.id === group.assigned_crew_id)?.name || 'Crew'
                            : ''}
                          {group.assigned_crew_id && group.assigned_employee_id ? ' · ' : ''}
                          {group.assigned_employee_id
                            ? (() => {
                                const emp = employees.find((e) => e.id === group.assigned_employee_id)
                                return emp ? formatEmployeeLabel(emp) : 'Employee'
                              })()
                            : ''}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {onAdvancedSettings && (
                      <button
                        type="button"
                        onClick={() => onAdvancedSettings(group.id)}
                        className="p-1 text-gray-500 hover:text-blue-600"
                        title="Checklist & advanced"
                      >
                        <Settings2Icon className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeGroup(group.id)}
                      className="p-1 text-gray-500 hover:text-red-600"
                      title="Remove group"
                    >
                      <Trash2Icon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-1.5 mb-2">
                  <select
                    value={group.assigned_crew_id || ''}
                    onChange={(e) =>
                      updateGroupAssignment(group.id, {
                        assigned_crew_id: e.target.value || null,
                      })
                    }
                    className="w-full px-1.5 py-1 text-[11px] border border-gray-300 rounded bg-white"
                    title="Crew override"
                  >
                    <option value="">Crew: Inherit</option>
                    {group.assigned_crew_id &&
                      !crews.some((c) => c.id === group.assigned_crew_id) && (
                        <option value={group.assigned_crew_id}>Crew: Assigned</option>
                      )}
                    {crews.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <select
                    value={group.assigned_employee_id || ''}
                    onChange={(e) =>
                      updateGroupAssignment(group.id, {
                        assigned_employee_id: e.target.value || null,
                      })
                    }
                    className="w-full px-1.5 py-1 text-[11px] border border-gray-300 rounded bg-white"
                    title="Employee override"
                  >
                    <option value="">Employee: Inherit</option>
                    {group.assigned_employee_id &&
                      !employees.some((e) => e.id === group.assigned_employee_id) && (
                        <option value={group.assigned_employee_id}>Employee: Assigned</option>
                      )}
                    {employees.map((e) => (
                      <option key={e.id} value={e.id}>
                        {formatEmployeeLabel(e)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {assignments.map((a) => (
                    <DraggableServiceChip
                      key={`${group.id}-${a.service_id}`}
                      serviceId={a.service_id}
                      serviceName={getServiceName(a.service_id)}
                      assignment={a}
                      sourceGroupId={group.id}
                    />
                  ))}
                  {assignments.length === 0 && (
                    <p className="text-xs text-gray-400 italic">Drop services here</p>
                  )}
                </div>
              </DroppableZone>
            )
          })}

          <DroppableZone
            id="new-group-zone"
            className="border-2 border-dashed border-gray-300 rounded-lg p-3 flex flex-col items-center justify-center min-h-[120px] text-gray-500"
          >
            <PlusIcon className="w-5 h-5 mb-1" />
            <p className="text-xs">Drop here to create new visit group</p>
          </DroppableZone>
        </div>

        <DroppableZone
          id="unassigned-pool"
          className="mt-3 border border-amber-200 rounded-lg p-3 bg-amber-50/50"
          label="Unassigned services"
        >
          <div className="flex flex-wrap gap-1.5">
            {unassignedIds.map((id) => (
              <DraggableServiceChip key={id} serviceId={id} serviceName={getServiceName(id)} />
            ))}
            {unassignedIds.length === 0 && (
              <p className="text-xs text-gray-500">All services are assigned to visit groups.</p>
            )}
          </div>
        </DroppableZone>

        <DragOverlay>
          {activeDrag && (
            <div className="px-3 py-1.5 rounded-full bg-blue-100 border border-blue-300 text-sm text-blue-800 shadow-lg">
              {activeDrag.serviceName}
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {projectedPreview.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs font-medium text-gray-600 mb-2">Next 4 weeks preview</p>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {projectedPreview.map((entry, i) => (
              <div key={i} className="text-xs text-gray-600 flex gap-2">
                <span className="font-medium text-gray-800 w-20 shrink-0">{entry.date}</span>
                <span>
                  {entry.groupName}: {entry.services.join(' + ')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {piggybackDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangleIcon className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-gray-900">Different schedule detected</h3>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>{piggybackDialog.serviceName}</strong> has a different schedule than the
                  target visit group. Attach as a <strong>piggyback</strong> task so it only runs
                  when its frequency matches (e.g. monthly on a day the crew is already there)?
                </p>
              </div>
            </div>
            <div className="flex gap-4 mb-3">
              <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="piggyback_mode"
                  checked={piggybackMode === 'nth_weekday'}
                  onChange={() => setPiggybackMode('nth_weekday')}
                />
                <span>Nth weekday</span>
              </label>
              <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="piggyback_mode"
                  checked={piggybackMode === 'day_of_month'}
                  onChange={() => setPiggybackMode('day_of_month')}
                />
                <span>Day of month</span>
              </label>
            </div>
            {piggybackMode === 'nth_weekday' ? (
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Occurrence</label>
                  <select
                    value={piggybackOccurrence}
                    onChange={(e) => setPiggybackOccurrence(parseInt(e.target.value))}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                  >
                    <option value={1}>1st</option>
                    <option value={2}>2nd</option>
                    <option value={3}>3rd</option>
                    <option value={4}>4th</option>
                    <option value={5}>Last</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Weekday</label>
                  <select
                    value={piggybackWeekday}
                    onChange={(e) => setPiggybackWeekday(parseInt(e.target.value))}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                  >
                    {DAY_LABELS.map((label, i) => (
                      <option key={i} value={i}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : (
              <div className="mb-3 flex items-center gap-2">
                <label className="text-xs font-medium text-gray-600">Day of month</label>
                <input
                  type="number"
                  min={1}
                  max={31}
                  value={piggybackDayOfMonth}
                  onChange={(e) =>
                    setPiggybackDayOfMonth(
                      Math.min(31, Math.max(1, parseInt(e.target.value, 10) || 1))
                    )
                  }
                  className="w-20 px-2 py-1.5 border border-gray-300 rounded text-sm"
                />
              </div>
            )}
            <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer mb-4">
              <input
                type="checkbox"
                checked={piggybackNearest}
                onChange={(e) => setPiggybackNearest(e.target.checked)}
              />
              <span>Run on the group's visit day closest to that date</span>
            </label>
            <p className="text-xs text-gray-500 mb-4 flex items-center gap-1">
              <LinkIcon className="w-3 h-3" />
              Piggyback: job visits still follow the group schedule; this task is added only on
              matching dates.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPiggybackDialog(null)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmPiggyback}
                className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700"
              >
                Attach as piggyback
              </button>
              <button
                type="button"
                onClick={() => {
                  onChange(
                    pruneEmptyVisitGroups(
                      moveServiceToGroup(
                        visitGroups,
                        piggybackDialog.serviceId,
                        piggybackDialog.targetGroupId
                      )
                    )
                  )
                  setPiggybackDialog(null)
                }}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Merge anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export { mergeGroups, syncGroupServiceIds }
